import { xmlDecl, el, escapeXml } from './index.js';
import { NS } from '../ns.js';
import type {
  Section,
  Paragraph,
  Run,
  TextRun,
  TableRun,
  PictureRun,
  SectionDef,
  Table,
  TableRow,
  TableCell,
  Picture,
  ColumnDef,
  HeaderFooterDef,
  SubDocument,
} from '../../../types.js';

export function generateSectionXml(section: Section, sectionIndex: number): string {
  return (
    xmlDecl() +
    '\n' +
    el(
      'hs:sec',
      {
        'xmlns:hs': NS.hs,
        'xmlns:hp': NS.hp,
        'xmlns:hc': NS.hc,
      },
      '\n' +
      generateSectionDef(section.def) +
      section.paragraphs.map((p) => generateParagraph(p)).join('\n') +
      '\n',
    )
  );
}

function generateSectionDef(def: SectionDef): string {
  const pagePr = el('hs:pagePr', {
    landscape: def.landscape ? 'LANDSCAPE' : 'PORTRAIT',
    width: String(def.pageWidth),
    height: String(def.pageHeight),
    gutterType: def.gutterType || 'LEFT_ONLY',
  });

  const pageMargin = el('hs:pageMargin', {
    left: String(def.pageMargin.left),
    right: String(def.pageMargin.right),
    top: String(def.pageMargin.top),
    bottom: String(def.pageMargin.bottom),
    header: String(def.pageMargin.header),
    footer: String(def.pageMargin.footer),
    gutter: String(def.pageMargin.gutter),
  });

  let extra = '';

  // 다단
  if (def.columns && def.columns.count > 1) {
    extra += generateColumns(def.columns) + '\n';
  }

  // 머리글/바닥글
  if (def.headerFooter) {
    if (def.headerFooter.header) {
      extra += generateHeaderFooter('hs:header', def.headerFooter.header) + '\n';
    }
    if (def.headerFooter.footer) {
      extra += generateHeaderFooter('hs:footer', def.headerFooter.footer) + '\n';
    }
  }

  return el(
    'hs:secPr',
    {},
    '\n' + pagePr + '\n' + pageMargin + '\n' + extra,
  ) + '\n';
}

function generateColumns(cols: ColumnDef): string {
  return el('hs:colPr', {
    type: cols.type || 'NORMAL',
    count: String(cols.count),
    gap: String(cols.gap || 0),
    sameSz: cols.sameSizes ? '1' : '0',
  });
}

function generateHeaderFooter(tag: string, subDoc: SubDocument): string {
  const paragraphs = subDoc.paragraphs.map((p) => generateParagraph(p)).join('\n');
  return el(tag, {}, '\n' + paragraphs + '\n');
}

function generateParagraph(para: Paragraph): string {
  const runs = para.runs.map((run) => generateRun(run)).join('');

  return el(
    'hp:p',
    {
      paraPrIDRef: String(para.paraPrIDRef),
      styleIDRef: String(para.styleIDRef),
    },
    '\n' + runs,
  );
}

function generateRun(run: Run): string {
  switch (run.t) {
    case 'text':
      return generateTextRun(run);
    case 'table':
      return generateTableRun(run);
    case 'picture':
      return generatePictureRun(run);
    case 'break':
      return generateBreakRun(run);
    default:
      return '';
  }
}

function generateTextRun(run: TextRun): string {
  return el(
    'hp:run',
    { charPrIDRef: String(run.charPrIDRef) },
    '\n' +
    el('hp:runText', {}, escapeXml(run.text)) +
    '\n',
  );
}

function generateTableRun(run: TableRun): string {
  return el(
    'hp:run',
    { charPrIDRef: String(run.charPrIDRef) },
    '\n' + generateTable(run.table) + '\n',
  );
}

function generatePictureRun(run: PictureRun): string {
  return el(
    'hp:run',
    { charPrIDRef: String(run.charPrIDRef) },
    '\n' + generatePicture(run.picture) + '\n',
  );
}

function generateBreakRun(run: Run & { t: 'break' }): string {
  const tagMap: Record<string, string> = {
    LINE: 'hp:lineBreak',
    PAGE: 'hp:pageBreak',
    COLUMN: 'hp:colBreak',
  };
  const tag = tagMap[run.breakType] || 'hp:lineBreak';
  return el(
    'hp:run',
    { charPrIDRef: String(run.charPrIDRef) },
    '\n' + `<${tag}/>` + '\n',
  );
}

// ─── Table Generation ───

function generateTable(table: Table): string {
  const colWidths = table.colWidths.map((w) => el('hp:colSz', { width: String(w) })).join('\n');

  const rows = table.rows.map((row, ri) => generateTableRow(row, ri)).join('\n');

  return el(
    'hp:tbl',
    {
      rowCnt: String(table.rowCount),
      colCnt: String(table.colCount),
      cellSpacing: table.cellSpacing !== undefined ? String(table.cellSpacing) : '0',
      borderFillIDRef: String(table.borderFillIDRef),
      width: String(table.width),
    },
    '\n' + colWidths + '\n' + rows + '\n',
  );
}

function generateTableRow(row: TableRow, rowIndex: number): string {
  const cells = row.cells.map((cell) => generateTableCell(cell)).join('\n');

  return el(
    'hp:tr',
    { height: String(row.height) },
    '\n' + cells + '\n',
  );
}

function generateTableCell(cell: TableCell): string {
  const paddingAttrs: Record<string, string | undefined> = {};
  if (cell.padding) {
    paddingAttrs['paddingLeft'] = String(cell.padding.left);
    paddingAttrs['paddingRight'] = String(cell.padding.right);
    paddingAttrs['paddingTop'] = String(cell.padding.top);
    paddingAttrs['paddingBottom'] = String(cell.padding.bottom);
  }

  const paragraphs = cell.paragraphs.map((p) => generateParagraph(p)).join('\n');

  return el(
    'hp:tc',
    {
      colSpan: String(cell.colSpan),
      rowSpan: String(cell.rowSpan),
      width: String(cell.width),
      height: String(cell.height),
      borderFillIDRef: String(cell.borderFillIDRef),
      ...paddingAttrs,
    },
    '\n' +
    el('hp:subList', {}, '\n' + paragraphs + '\n') +
    '\n',
  );
}

// ─── Picture Generation ───

function generatePicture(pic: Picture): string {
  return el(
    'hp:pic',
    {
      binDataIDRef: String(pic.binDataIDRef),
      width: String(pic.width),
      height: String(pic.height),
      offsetX: pic.offsetX !== undefined ? String(pic.offsetX) : undefined,
      offsetY: pic.offsetY !== undefined ? String(pic.offsetY) : undefined,
    },
  );
}
